/*
 * Splectrum Picks — <pick-carousel> (sections/pick-carousel.liquid).
 *
 * Infinite loop on top of a native scroll-snap track, no libraries:
 *   - the last C and first C cards are cloned to the head and tail of the track
 *     (C = cards per view + 2, capped at the number of cards);
 *   - the track starts on the first real card;
 *   - whenever scrolling settles on a clone (scrollend, or a debounced scroll
 *     fallback) the scroll position is shifted by one full period so the same
 *     card is now shown from the real set — visually nothing moves;
 *   - a mid-scroll guard performs the same shift early if a long fling runs
 *     deep into the clones, so the user can never hit the end of the track.
 *   Arrows / keyboard step one card with smooth scrolling. Resize rebuilds the
 *   clones (cards per view can change) and re-anchors on the current card.
 *   Fewer cards than one view: no clones, no arrows, static row.
 *   Autoplay (optional) never starts under prefers-reduced-motion and pauses on
 *   hover, focus, touch and when the tab is hidden or the carousel off-screen.
 *
 * Motion layers (both optional section settings, both off under reduced motion):
 *   - Cylinder depth (data-3d / data-depth): every card is transformed as a
 *     function of its distance from the centre of the viewport — rotateY toward
 *     the centre, pushed back (translateZ), slightly smaller and dimmer at the
 *     edges — so the row reads as the front of a slowly turning drum. The
 *     middle cards stay flat. Transforms only, written once per frame (rAF)
 *     from one scrollLeft read; nothing touches layout. Per-card perspective()
 *     is used because a scroll container cannot carry preserve-3d.
 *   - Pointer drift (data-drift / data-drift-strength): fine-pointer devices
 *     only. The mouse's horizontal position over the carousel sets a target
 *     scroll velocity (dead zone in the middle, quadratic ramp to the edges);
 *     the actual velocity eases toward it so it feels weighted. Scroll snapping
 *     is suspended while drifting and restored after settling on the nearest
 *     card when the pointer leaves, parks in the dead zone, or a modal opens.
 *     Wheel/trackpad input and the arrows take precedence over drift.
 *
 * Quick-add modals must not sit under a transformed card (position: fixed
 * would be trapped by it). Craft's ModalDialog already moves every modal to
 * <body> in its connectedCallback; hoistModals() below is a fallback for any
 * that are still in the track when we build. Openers find modals by id, so
 * clones share the originals' modals.
 */
(function () {
  if (customElements.get('pick-carousel')) return;

  const SUPPORTS_SCROLLEND = 'onscrollend' in window;

  class PickCarousel extends HTMLElement {
    constructor() {
      super();
      this.onScroll = this.onScroll.bind(this);
      this.onSettle = this.onSettle.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.pause = this.pause.bind(this);
      this.resume = this.resume.bind(this);
      this.apply3d = this.apply3d.bind(this);
      this.driftFrame = this.driftFrame.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onWheel = this.onWheel.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('.pick-carousel__track');
      if (!this.track) return;
      this.section = this.closest('.pick-carousel');
      this.prevBtn = this.querySelector('.pick-carousel__arrow--prev');
      this.nextBtn = this.querySelector('.pick-carousel__arrow--next');
      this.real = Array.from(this.track.children).filter((el) => !el.hasAttribute('data-pc-clone'));
      this.n = this.real.length;
      this.items = this.real;
      this.c = 0;
      this.index = 0; // current real card (0..n-1)
      this.loop = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.settleTimer = null;
      this.resizeTimer = null;
      this.autoplayTimer = null;
      this.paused = false;
      this.inView = true;
      this.cloneSerial = 0;
      this.suspended = false; // true while a resize / rebuild is in flight

      // Motion settings.
      const pct = (attr, fallback) => {
        const v = parseInt(this.getAttribute(attr), 10);
        return Math.min(100, Math.max(0, isNaN(v) ? fallback : v)) / 100;
      };
      this.enable3d = this.getAttribute('data-3d') === 'true' && !this.reducedMotion.matches;
      this.depth = pct('data-depth', 50);
      if (this.depth === 0) this.enable3d = false;
      this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.driftEnabled =
        this.getAttribute('data-drift') === 'true' && !this.reducedMotion.matches && this.finePointer.matches;
      this.driftStrength = pct('data-drift-strength', 50);
      if (this.driftStrength === 0) this.driftEnabled = false;
      // px/s at the very edge of the track; the ramp is quadratic so the
      // middle of the live zone is much gentler.
      this.maxSpeed = 160 + 760 * this.driftStrength;
      this.rafId = null;
      this.cards = null;
      this.centers = null;
      this.drifting = false;
      this.driftRaf = null;
      this.driftTarget = 0;
      this.driftV = 0;
      this.driftAcc = 0;
      this.lastT = 0;
      this.wheelUntil = 0;
      this.snapRestoreTimer = null;
      this.rectCache = null;
      this.rectAt = 0;

      this.hoistModals();
      this.build();

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      if (SUPPORTS_SCROLLEND) this.track.addEventListener('scrollend', this.onSettle);
      this.track.addEventListener('keydown', this.onKeydown);
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1, true));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1, true));

      if (this.enable3d && this.section) this.section.classList.add('pick-carousel--3d');

      if (this.driftEnabled) {
        this.addEventListener('pointermove', this.onPointerMove);
        this.addEventListener('pointerleave', this.onPointerLeave);
        this.addEventListener('pointercancel', this.onPointerLeave);
        this.addEventListener('wheel', this.onWheel, { passive: true });
        this.addEventListener('pointerdown', this.onWheel); // a click / drag also wins over drift
      }

      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(this.onResize);
        this.ro.observe(this.track);
      } else {
        window.addEventListener('resize', this.onResize);
      }

      this.setupAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.ro) this.ro.disconnect();
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.io) this.io.disconnect();
      if (this.rafId) cancelAnimationFrame(this.rafId);
      if (this.driftRaf) cancelAnimationFrame(this.driftRaf);
      clearTimeout(this.snapRestoreTimer);
      // Theme editor re-renders: take our hoisted modals with us so the fresh
      // instance's copies are the ones the openers find.
      (this.hoisted || []).forEach((modal) => modal.remove());
    }

    /* ---------------------------------------------------------------- */
    /* Quick-add modals                                                   */
    /* ---------------------------------------------------------------- */
    hoistModals() {
      this.hoisted = [];
      this.track.querySelectorAll('quick-add-modal').forEach((modal) => {
        const id = modal.getAttribute('id');
        if (id) {
          document.querySelectorAll('body > quick-add-modal').forEach((existing) => {
            if (existing.getAttribute('id') === id) existing.remove();
          });
        }
        document.body.appendChild(modal);
        this.hoisted.push(modal);
      });
    }

    modalOpen() {
      return (
        document.body.classList.contains('overflow-hidden') ||
        !!document.querySelector('quick-add-modal[open], cart-drawer.active, cart-drawer.animate')
      );
    }

    /* ---------------------------------------------------------------- */
    /* Build                                                              */
    /* ---------------------------------------------------------------- */
    perView() {
      const raw = getComputedStyle(this.section || this).getPropertyValue('--pc-cols');
      const cols = parseInt(raw, 10);
      return cols > 0 ? cols : 1;
    }

    build() {
      this.suspended = true;
      // Drop any existing clones.
      Array.from(this.track.querySelectorAll('[data-pc-clone]')).forEach((el) => el.remove());

      const cols = this.perView();
      this.cols = cols;
      this.loop = this.n > cols;

      if (!this.loop) {
        this.items = this.real;
        this.c = 0;
        this.measure();
        if (this.section) this.section.classList.add('pick-carousel--static');
        this.track.scrollLeft = 0;
        this.suspended = false;
        this.schedule3d();
        return;
      }
      if (this.section) this.section.classList.remove('pick-carousel--static');

      const c = Math.min(this.n, cols + 2);
      this.c = c;
      this.cloneSerial += 1;
      const serial = this.cloneSerial;

      const head = document.createDocumentFragment();
      for (let i = this.n - c; i < this.n; i++) head.appendChild(this.makeClone(this.real[i], 'h' + serial));
      const tail = document.createDocumentFragment();
      for (let i = 0; i < c; i++) tail.appendChild(this.makeClone(this.real[i], 't' + serial));
      this.track.insertBefore(head, this.real[0]);
      this.track.appendChild(tail);

      this.items = Array.from(this.track.children);
      this.measure();
      this.jumpTo(this.c + this.index);
      this.schedule3d();
      requestAnimationFrame(() => {
        this.suspended = false;
      });
    }

    makeClone(source, tag) {
      const clone = source.cloneNode(true);
      clone.setAttribute('data-pc-clone', tag);
      clone.setAttribute('aria-hidden', 'true');

      // Keep ids unique: suffix every id and fix the attributes that point at them.
      const suffix = '-pc' + tag;
      const map = {};
      clone.querySelectorAll('[id]').forEach((el) => {
        const id = el.getAttribute('id');
        map[id] = id + suffix;
        el.setAttribute('id', id + suffix);
      });
      const refAttrs = ['data-modal', 'aria-controls', 'aria-labelledby', 'aria-describedby', 'for', 'form', 'href'];
      clone.querySelectorAll('*').forEach((el) => {
        refAttrs.forEach((attr) => {
          if (!el.hasAttribute(attr)) return;
          const value = el.getAttribute(attr);
          if (attr === 'href' && value.charAt(0) !== '#') return;
          const rewritten = value
            .split(' ')
            .map((token) => {
              const hash = token.charAt(0) === '#';
              const key = hash ? token.slice(1) : token;
              if (map[key]) return (hash ? '#' : '') + map[key];
              return token;
            })
            .join(' ');
          el.setAttribute(attr, rewritten);
        });
      });
      // Out of the tab order (mouse / touch still works).
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });
      // A fresh card starts flat; apply3d() positions it on the next frame.
      const card = clone.querySelector('.card-wrapper') || clone.firstElementChild;
      if (card) {
        card.style.transform = '';
        card.style.transformOrigin = '';
        card.style.opacity = '';
      }
      return clone;
    }

    /* ---------------------------------------------------------------- */
    /* Geometry                                                           */
    /* ---------------------------------------------------------------- */
    measure() {
      const origin = this.items.length ? this.items[0].offsetLeft : 0;
      this.positions = this.items.map((item) => item.offsetLeft - origin);
      // Card centres in track scroll space (track is the offsetParent), for 3D.
      this.centers = this.items.map((item) => item.offsetLeft + item.offsetWidth / 2);
      // Some slides carry injected app markup (Judge.me badge styles) before the
      // card, so look the wrapper up by class and only fall back to firstElementChild.
      this.cards = this.items.map((item) => item.querySelector('.card-wrapper') || item.firstElementChild);
      this.cards.forEach((card) => {
        if (card) card._pc3 = undefined;
      });
    }

    pos(i) {
      if (!this.positions || this.positions.length !== this.items.length) this.measure();
      return this.positions[i] || 0;
    }

    period() {
      return this.pos(this.c + this.n) - this.pos(this.c);
    }

    nearest() {
      const s = this.track.scrollLeft;
      let best = 0;
      let bestDist = Infinity;
      for (let i = 0; i < this.items.length; i++) {
        const d = Math.abs(this.pos(i) - s);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      }
      return best;
    }

    jumpTo(i) {
      this.track.scrollLeft = this.pos(i);
    }

    /* Shift the scroll position by a period when sitting on a clone. */
    normalize() {
      if (!this.loop) return;
      const s = this.track.scrollLeft;
      const start = this.pos(this.c);
      const end = this.pos(this.c + this.n);
      const period = this.period();
      if (period <= 0) return;
      if (s < start - 1) {
        this.track.scrollLeft = s + period;
      } else if (s >= end - 1) {
        this.track.scrollLeft = s - period;
      }
      const i = this.nearest();
      this.index = ((i - this.c) % this.n + this.n) % this.n;
    }

    /* ---------------------------------------------------------------- */
    /* Cylinder depth                                                     */
    /* ---------------------------------------------------------------- */
    schedule3d() {
      if (!this.enable3d || this.rafId !== null) return;
      this.rafId = requestAnimationFrame(this.apply3d);
    }

    apply3d() {
      this.rafId = null;
      if (!this.enable3d || !this.cards || !this.centers) return;
      // One layout read per frame, then writes only.
      const half = this.track.clientWidth / 2;
      if (half <= 0) return;
      const cx = this.track.scrollLeft + half;
      const maxAngle = 42 * this.depth; // deg at the far edge
      const maxZ = 160 * this.depth; // px pushed back
      const maxScale = 0.1 * this.depth;
      const maxFade = 0.45 * this.depth;
      const dead = 0.08; // only the very centre stays flat; size falls off continuously from there
      const ramp = 1.05; // reaches full turn just past the edge (the peeking cards)
      for (let i = 0; i < this.cards.length; i++) {
        const card = this.cards[i];
        if (!card) continue;
        const d = (this.centers[i] - cx) / half;
        const ad = Math.abs(d);
        let t = ad <= dead ? 0 : Math.min(1, (ad - dead) / ramp);
        const e = t * t * (3 - 2 * t); // smoothstep
        const sign = d < 0 ? -1 : 1;
        const key = Math.round(e * 100) * sign;
        if (card._pc3 === key) continue;
        card._pc3 = key;
        if (e === 0) {
          card.style.transform = '';
          card.style.transformOrigin = '';
          card.style.opacity = '';
          continue;
        }
        // Pivot on the edge nearest the centre of the track. Rotating about the
        // card's own centre pushes the inner edge toward the viewer, which made
        // the peeking half-cards render larger than the full cards beside them.
        // With the pivot on the inner edge only the outer edge recedes, so
        // apparent size falls off monotonically from the centre outward.
        card.style.transformOrigin = sign > 0 ? 'left center' : 'right center';
        card.style.transform =
          'perspective(1100px) rotateY(' +
          (sign * e * maxAngle).toFixed(2) +
          'deg) translateZ(' +
          (-e * maxZ).toFixed(1) +
          'px) scale(' +
          (1 - e * maxScale).toFixed(4) +
          ')';
        card.style.opacity = (1 - e * maxFade).toFixed(3);
      }
    }

    /* ---------------------------------------------------------------- */
    /* Pointer drift                                                      */
    /* ---------------------------------------------------------------- */
    trackRect() {
      const now = performance.now();
      if (!this.rectCache || now - this.rectAt > 250) {
        this.rectCache = this.track.getBoundingClientRect();
        this.rectAt = now;
      }
      return this.rectCache;
    }

    onPointerMove(event) {
      if (!this.loop || event.pointerType !== 'mouse') return;
      if (this.modalOpen() || (event.target.closest && event.target.closest('.pick-carousel__nav, quick-add-modal'))) {
        this.driftTarget = 0;
        return;
      }
      const rect = this.trackRect();
      if (rect.width <= 0) return;
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1; // -1 .. 1
      const dz = 0.3; // middle 30% is still
      const ax = Math.abs(x);
      let target = 0;
      if (ax > dz) {
        const u = Math.min(1, (ax - dz) / (1 - dz));
        target = (x < 0 ? -1 : 1) * u * u * this.maxSpeed;
      }
      this.driftTarget = target;
      if (target !== 0 && !this.drifting && !this.suspended) this.startDrift();
    }

    onPointerLeave() {
      this.driftTarget = 0;
      this.rectCache = null;
    }

    onWheel() {
      // Wheel / trackpad / click: let the user's input win for a moment.
      this.wheelUntil = performance.now() + 700;
    }

    startDrift() {
      this.drifting = true;
      this.driftV = 0;
      this.driftAcc = 0;
      this.lastT = performance.now();
      clearTimeout(this.snapRestoreTimer);
      this.track.classList.add('is-drifting');
      this.stopAutoplay();
      this.driftRaf = requestAnimationFrame(this.driftFrame);
    }

    driftFrame(now) {
      if (!this.drifting) return;
      const dt = Math.min(0.05, Math.max(0.001, (now - this.lastT) / 1000));
      this.lastT = now;
      let target = this.driftTarget;
      if (this.suspended || now < this.wheelUntil || this.modalOpen()) target = 0;
      // Frame-rate independent easing: slow to pick up, a little quicker to shed.
      const rate = target === 0 ? 4.5 : 3;
      this.driftV += (target - this.driftV) * (1 - Math.exp(-rate * dt));
      if (target === 0 && Math.abs(this.driftV) < 6) {
        this.stopDrift(true);
        return;
      }
      // Whole pixels only; keep the remainder so slow drift still moves.
      this.driftAcc += this.driftV * dt;
      const whole = this.driftAcc < 0 ? Math.ceil(this.driftAcc) : Math.floor(this.driftAcc);
      if (whole !== 0) {
        this.track.scrollLeft += whole;
        this.driftAcc -= whole;
      }
      this.driftRaf = requestAnimationFrame(this.driftFrame);
    }

    /* settle = glide onto the nearest card before snapping is switched back on. */
    stopDrift(settle) {
      if (!this.drifting) return;
      this.drifting = false;
      if (this.driftRaf) cancelAnimationFrame(this.driftRaf);
      this.driftRaf = null;
      this.driftV = 0;
      this.driftAcc = 0;
      this.normalize();
      if (settle) this.scrollToItem(this.nearest());
      clearTimeout(this.snapRestoreTimer);
      this.snapRestoreTimer = setTimeout(() => {
        this.track.classList.remove('is-drifting');
        this.startAutoplay();
      }, 500);
    }

    /* ---------------------------------------------------------------- */
    /* Events                                                             */
    /* ---------------------------------------------------------------- */
    onScroll() {
      if (this.suspended) return;
      if (this.loop) {
        // Deep-clone guard: a long fling must never reach the end of the track.
        const s = this.track.scrollLeft;
        const period = this.period();
        // One card of tolerance past the real set in either direction: a single
        // arrow step never trips it, a long fling does (and gets re-anchored).
        const lo = this.pos(this.c - 2);
        const hi = this.pos(this.c + this.n + 1);
        if (s < lo) {
          this.track.scrollLeft = s + period;
        } else if (s > hi) {
          this.track.scrollLeft = s - period;
        }
      }
      this.schedule3d();
      if (!SUPPORTS_SCROLLEND) {
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(this.onSettle, 160);
      }
    }

    onSettle() {
      if (this.suspended || this.drifting) return;
      this.normalize();
      this.schedule3d();
    }

    onKeydown(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.step(1, true);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.step(-1, true);
      }
    }

    onResize() {
      // Positions are stale until the layout settles: ignore scroll events
      // (which the browser fires while the track changes width) until then.
      this.suspended = true;
      this.rectCache = null;
      if (this.drifting) this.stopDrift(false);
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        const cols = this.perView();
        if (cols !== this.cols) {
          this.build();
        } else {
          this.measure();
          if (this.loop) this.jumpTo(this.c + this.index);
          this.schedule3d();
        }
        requestAnimationFrame(() => {
          this.suspended = false;
        });
      }, 120);
    }

    step(dir, user) {
      if (user) this.restartAutoplay();
      if (this.drifting) this.stopDrift(false);
      if (!this.loop) {
        const target = Math.max(0, Math.min(this.items.length - 1, this.nearest() + dir));
        this.scrollToItem(target);
        return;
      }
      this.normalize();
      const target = Math.max(0, Math.min(this.items.length - 1, this.nearest() + dir));
      this.scrollToItem(target);
    }

    scrollToItem(i) {
      const behavior = this.reducedMotion.matches ? 'auto' : 'smooth';
      this.track.scrollTo({ left: this.pos(i), behavior: behavior });
    }

    /* ---------------------------------------------------------------- */
    /* Autoplay                                                           */
    /* ---------------------------------------------------------------- */
    setupAutoplay() {
      this.autoplay = this.getAttribute('data-autoplay') === 'true' && this.loop && !this.reducedMotion.matches;
      if (!this.autoplay) return;
      this.speed = Math.max(2, parseInt(this.getAttribute('data-autoplay-speed'), 10) || 5) * 1000;

      this.addEventListener('pointerenter', this.pause);
      this.addEventListener('pointerleave', this.resume);
      this.addEventListener('focusin', this.pause);
      this.addEventListener('focusout', this.resume);
      this.addEventListener('touchstart', this.pause, { passive: true });
      this.addEventListener('touchend', this.resume, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibility);
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          this.inView = entries.some((e) => e.isIntersecting);
          this.inView ? this.startAutoplay() : this.stopAutoplay();
        }, { threshold: 0.3 });
        this.io.observe(this);
      } else {
        this.startAutoplay();
      }
    }

    startAutoplay() {
      if (!this.autoplay || this.paused || !this.inView || this.autoplayTimer || this.drifting) return;
      this.autoplayTimer = setInterval(() => this.step(1, false), this.speed);
    }

    stopAutoplay() {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    restartAutoplay() {
      if (!this.autoplay) return;
      this.stopAutoplay();
      this.startAutoplay();
    }

    pause() {
      this.paused = true;
      this.stopAutoplay();
    }

    resume() {
      this.paused = false;
      this.startAutoplay();
    }

    onVisibility() {
      document.hidden ? this.stopAutoplay() : this.startAutoplay();
    }
  }

  customElements.define('pick-carousel', PickCarousel);
})();
